import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { Observable, of } from 'rxjs';
import { UserContextService } from 'src/services/user-context.service';
import { MultiIndustryService } from 'src/services/multi-industry.service';

/**
 * Profile Resolver
 *
 * Pre-loads profile-related context to prevent FOUC on profile routes.
 * Determines which profile tabs are available based on user permissions.
 */
export interface ProfileData {
    isInsuranceIndustry: boolean;
    hasPermissionForCarrierBranding: boolean;
    hasPermissionForProfile: boolean;
    accountType: 'agency' | 'carrier' | 'firm' | 'education' | 'unknown';
    error?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ProfileResolverService implements Resolve<ProfileData> {

    constructor(
        private userContextService: UserContextService,
        private multiIndustryService: MultiIndustryService
    ) {}

    resolve(): Observable<ProfileData> {
        try {
            // All these checks are synchronous (no HTTP calls)
            const isInsuranceIndustry = this.multiIndustryService.isInsuranceIndustry();
            const hasPermissionForCarrierBranding = this.userContextService.hasPermissionToViewComponent('carrierBranding');
            const hasPermissionForProfile = this.userContextService.hasPermissionToViewComponent('profile');

            // Determine account type
            let accountType: 'agency' | 'carrier' | 'firm' | 'education' | 'unknown' = 'unknown';
            if (this.userContextService.isAgencyCapabilitiesAccount()) {
                accountType = 'agency';
            } else if (this.userContextService.isCarrierAccount()) {
                accountType = 'carrier';
            } else if (this.userContextService.isFirmCapabilitiesAccount()) {
                accountType = 'firm';
            } else if (this.userContextService.isEducationProviderAccount()) {
                accountType = 'education';
            }

            return of({
                isInsuranceIndustry,
                hasPermissionForCarrierBranding,
                hasPermissionForProfile,
                accountType,
                error: false
            });
        } catch (error) {
            console.error('[ProfileResolver] Failed to resolve profile data:', error);
            return of({
                isInsuranceIndustry: false,
                hasPermissionForCarrierBranding: false,
                hasPermissionForProfile: false,
                accountType: 'unknown',
                error: true
            });
        }
    }
}
